import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, message, Spin } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import { Dropdown } from '@/components/AntdCompatDropdown';
import SearchInput from '@/components/SearchInput';
import EllipsisTooltip from '@/components/EllipsisTooltip';
import {
  ChevronDownSmallIcon,
  CloseIcon,
  PlusCircleIcon,
} from '@/assets/icons/icons';
import type { PickerCatalogProps } from '../productPickerCatalog';
import styles from './ProductPicker.module.css';

const VISIBLE_CHIP_LIMIT = 3;

/** Props of `ProductPicker`: a selection plus the catalogue it picks from. */
export interface ProductPickerProps extends PickerCatalogProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  getProductName: (id: string) => string;
  loadAllProductIds: () => Promise<string[]>;
  onOpenAddProduct?: () => void;
  ariaLabel?: string;
}

/** Multi-select over the company's product catalogue (Figma 46018:455436). */
const ProductPicker: React.FC<ProductPickerProps> = ({
  selectedIds,
  onChange,
  options,
  catalogTotalCount,
  loading,
  search,
  searchInputKey,
  onSearch,
  onScroll,
  getProductName,
  loadAllProductIds,
  onOpenAddProduct,
  ariaLabel = 'Products',
}) => {
  const [open, setOpen] = useState(false);
  const [allLoading, setAllLoading] = useState(false);
  const [menuWidth, setMenuWidth] = useState<number | undefined>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const selected = new Set(selectedIds);
  const allSelected =
    catalogTotalCount > 0 && selectedIds.length >= catalogTotalCount;

  const setDropdownOpen = (visible: boolean): void => {
    setOpen(visible);
    if (visible && triggerRef.current) {
      setMenuWidth(triggerRef.current.clientWidth);
    }
    if (!visible && search) {
      onSearch('');
    }
  };

  const toggle = (id: string): void => {
    onChange(
      selected.has(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id]
    );
  };

  const selectAll = async (): Promise<void> => {
    setAllLoading(true);
    try {
      const ids = await loadAllProductIds();
      if (mountedRef.current) {
        onChange([...selectedIds, ...ids.filter((id) => !selected.has(id))]);
      }
    } catch (error) {
      console.error(error);
      if (mountedRef.current) message.error("Couldn't load all products");
    } finally {
      if (mountedRef.current) setAllLoading(false);
    }
  };

  const toggleAll = (): void => {
    if (allSelected) {
      onChange([]);
      return;
    }
    const _ = selectAll();
  };

  const remove = (id: string): void => {
    onChange(selectedIds.filter((selectedId) => selectedId !== id));
  };

  const hiddenCount = Math.max(0, selectedIds.length - VISIBLE_CHIP_LIMIT);

  const menu = (
    <div className={styles.menu} onClick={(e) => e.stopPropagation()}>
      <div className={styles.searchRow}>
        <SearchInput
          key={`product-picker-search-${searchInputKey}`}
          placeholder='Search product'
          onChange={onSearch}
          onClear={() => onSearch('')}
          defaultValue={search}
          width='100%'
        />
      </div>
      <div className={styles.divider} />
      <div className={styles.options} onScroll={onScroll}>
        {!search && (
          <Checkbox
            className={`checkbox-sm ${styles.option} ${styles.isAll}`}
            checked={allSelected}
            disabled={allLoading}
            onChange={toggleAll}
          >
            <span className={styles.optionText}>
              {allLoading
                ? 'Loading all products…'
                : `All products (${catalogTotalCount})`}
            </span>
            {allLoading && <Spin size='small' />}
          </Checkbox>
        )}
        {options.map((product) => (
          <Checkbox
            key={product.id}
            className={`checkbox-sm ${styles.option}`}
            checked={selected.has(product.id)}
            onChange={() => toggle(product.id)}
          >
            <EllipsisTooltip
              text={product.name}
              className={styles.optionText}
            />
          </Checkbox>
        ))}
        {loading && (
          <div className={styles.loading}>
            <Spin size='small' />
          </div>
        )}
        {!loading && search && options.length === 0 && (
          <div className={styles.empty}>No products found</div>
        )}
      </div>
      {onOpenAddProduct && (
        <button
          type='button'
          className={styles.add}
          onClick={() => {
            setDropdownOpen(false);
            onOpenAddProduct();
          }}
        >
          <PlusCircleIcon />
          <span>Add new product</span>
        </button>
      )}
    </div>
  );

  return (
    <div className={`${styles.field} ${open ? styles.isOpen : ''}`.trim()}>
      <Dropdown
        overlay={menu}
        trigger={['click']}
        visible={open}
        onVisibleChange={setDropdownOpen}
        overlayStyle={menuWidth ? { width: menuWidth } : undefined}
      >
        <button
          ref={triggerRef}
          type='button'
          className={styles.toggle}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup='listbox'
        />
      </Dropdown>
      <div className={styles.values}>
        {selectedIds.length === 0 ? (
          <span className={styles.placeholder}>Select</span>
        ) : (
          selectedIds.slice(0, VISIBLE_CHIP_LIMIT).map((id) => (
            <span key={id} className={styles.chip}>
              <EllipsisTooltip
                text={getProductName(id)}
                className={styles.chipText}
              />
              <button
                type='button'
                aria-label={`Remove ${getProductName(id)}`}
                className={styles.chipRemove}
                onClick={() => remove(id)}
              >
                <CloseIcon />
              </button>
            </span>
          ))
        )}
        {hiddenCount > 0 && (
          <span className={`${styles.chip} ${styles.isMore}`}>
            +{hiddenCount} more
          </span>
        )}
      </div>
      <div className={styles.actions}>
        {selectedIds.length > 0 && (
          <button
            type='button'
            className={styles.action}
            aria-label='Clear selected products'
            onClick={() => onChange([])}
          >
            <CloseCircleFilled className={styles.clear} />
          </button>
        )}
        <span className={styles.chevron}>
          <ChevronDownSmallIcon size={16} color='var(--text-secondary)' />
        </span>
      </div>
    </div>
  );
};

export default ProductPicker;
